import { ACTION_NAMES, DEFAULT_KEYS, keyLabel, type Action } from "../input/bindings";
import type { TouchPreference } from "../input/Touch";

export interface Settings {
  /** KeyboardEvent.code strings per action; any of them fires it. */
  keys: Record<Action, string[]>;
  /** Turret aim speed under the mouse, 0.25..2. */
  turretSensitivity: number;
  /** Each 0..1; sfx and music are scaled by master. */
  volume: { master: number; sfx: number; music: number };
  bloom: boolean;
  /** Whether the camera starts out turning with the ship you're aboard (V still switches). */
  cameraTurns: boolean;
  /** The touch controls: on touch screens (auto), always, or never. */
  touch: TouchPreference;
}

const TOUCH_PREFERENCES: readonly TouchPreference[] = ["auto", "on", "off"];

/** Versioned: a change to the default keys starts everyone afresh. */
const STORAGE_KEY = "dyson.settings.3";
const ACTIONS = Object.keys(DEFAULT_KEYS) as Action[];

/** A fresh copy of the defaults, so callers can change it freely. */
export function defaultSettings(): Settings {
  return {
    keys: copyKeys(DEFAULT_KEYS),
    turretSensitivity: 1,
    touch: "auto",
    volume: { master: 1, sfx: 0.8, music: 0.5 },
    bloom: true,
    cameraTurns: true,
  };
}

/**
 * The saved settings, laid over the defaults: anything missing or garbled
 * (a key added since, a hand-edited value) falls back to its
 * default rather than losing the rest. Never throws.
 */
export function loadSettings(): Settings {
  const s = defaultSettings();
  let raw: unknown;
  try {
    raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
  } catch {
    return s;
  }
  if (!raw || typeof raw !== "object") return s;
  const r = raw as Partial<Record<keyof Settings, unknown>>;
  const keys = r.keys && typeof r.keys === "object" ? (r.keys as Record<string, unknown>) : {};
  for (const a of ACTIONS) {
    const k = keys[a];
    if (Array.isArray(k) && k.length && k.every((c) => typeof c === "string" && c)) s.keys[a] = [...(k as string[])];
  }
  s.turretSensitivity = num(r.turretSensitivity, 0.25, 2, s.turretSensitivity);
  const v = r.volume && typeof r.volume === "object" ? (r.volume as Record<string, unknown>) : {};
  s.volume = { master: num(v.master, 0, 1, s.volume.master), sfx: num(v.sfx, 0, 1, s.volume.sfx), music: num(v.music, 0, 1, s.volume.music) };
  if (typeof r.bloom === "boolean") s.bloom = r.bloom;
  if (typeof r.cameraTurns === "boolean") s.cameraTurns = r.cameraTurns;
  if (TOUCH_PREFERENCES.includes(r.touch as TouchPreference)) s.touch = r.touch as TouchPreference;
  return s;
}

/** Best effort: private windows and full storage just don't remember. */
export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Not remembered, but still applied for this session.
  }
}

function num(x: unknown, min: number, max: number, fallback: number): number {
  return typeof x === "number" && Number.isFinite(x) ? Math.min(max, Math.max(min, x)) : fallback;
}

function copyKeys(keys: Record<Action, string[]>): Record<Action, string[]> {
  return Object.fromEntries(ACTIONS.map((a) => [a, [...keys[a]]])) as Record<Action, string[]>;
}

/**
 * Settings, opened with O or the gear in the top-right corner: key bindings,
 * turret mouse speed, volumes and a couple of display switches. Every change
 * is saved at once and passed to `onChange`, so there's no apply button.
 */
export class SettingsPanel {
  onChange: (s: Settings) => void = () => {};

  private readonly root = document.getElementById("settings")!;
  private readonly keyList = this.root.querySelector<HTMLElement>(".settings-keys")!;
  /** The action waiting for its new key, while "change" is pressed. */
  private listening: Action | null = null;
  private readonly rows = new Map<Action, { chips: HTMLElement; button: HTMLButtonElement }>();

  constructor(private readonly settings: Settings) {
    this.root.querySelector(".settings-close")!.addEventListener("click", () => this.close());
    document.getElementById("hud-settings")?.addEventListener("click", () => this.toggle());
    this.root.querySelector(".settings-reset")!.addEventListener("click", () => {
      this.settings.keys = copyKeys(DEFAULT_KEYS);
      this.listening = null;
      this.drawKeys();
      this.changed();
    });

    for (const a of ACTIONS) {
      const row = document.createElement("div");
      row.className = "settings-row";
      const name = document.createElement("span");
      name.className = "settings-name";
      name.textContent = ACTION_NAMES[a];
      const chips = document.createElement("span");
      chips.className = "settings-chips";
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = "change";
      button.addEventListener("click", () => {
        this.listening = this.listening === a ? null : a;
        this.drawKeys();
      });
      row.append(name, chips, button);
      this.keyList.append(row);
      this.rows.set(a, { chips, button });
    }
    this.drawKeys();

    this.slider("turret", settings.turretSensitivity, (v) => (settings.turretSensitivity = v), (v) => `${v.toFixed(2)}×`);
    this.slider("master", settings.volume.master, (v) => (settings.volume.master = v), percent);
    this.slider("sfx", settings.volume.sfx, (v) => (settings.volume.sfx = v), percent);
    this.slider("music", settings.volume.music, (v) => (settings.volume.music = v), percent);
    this.checkbox("bloom", settings.bloom, (v) => (settings.bloom = v));
    this.checkbox("camera", settings.cameraTurns, (v) => (settings.cameraTurns = v));
    const touch = this.root.querySelector<HTMLSelectElement>(".settings-touch")!;
    touch.value = settings.touch;
    touch.addEventListener("change", () => {
      settings.touch = TOUCH_PREFERENCES.includes(touch.value as TouchPreference) ? (touch.value as TouchPreference) : "auto";
      this.changed();
      touch.blur();
    });

    // Capture, so the key being bound never reaches the game (or the
    // settings toggle itself). Escape cancels a rebind, or else closes us.
    window.addEventListener(
      "keydown",
      (e) => {
        if (!this.isOpen) return;
        if (this.listening) {
          e.stopPropagation();
          e.preventDefault();
          if (e.code !== "Escape") this.settings.keys[this.listening] = [e.code];
          const bound = e.code !== "Escape";
          this.listening = null;
          this.drawKeys();
          if (bound) this.changed();
        } else if (e.code === "Escape") {
          e.stopPropagation();
          this.close();
        }
      },
      true,
    );
  }

  get isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.root.classList.remove("hidden");
  }

  close(): void {
    this.listening = null;
    this.drawKeys();
    this.root.classList.add("hidden");
  }

  private changed(): void {
    saveSettings(this.settings);
    this.onChange(this.settings);
  }

  private drawKeys(): void {
    for (const [a, { chips, button }] of this.rows) {
      const waiting = this.listening === a;
      chips.replaceChildren(
        ...(waiting ? ["press a key… (Esc cancels)"] : this.settings.keys[a]).map((code) => {
          const chip = document.createElement("span");
          chip.className = waiting ? "chip waiting" : "chip";
          chip.textContent = waiting ? code : keyLabel(code);
          return chip;
        }),
      );
      button.classList.toggle("on", waiting);
    }
  }

  /** Wire up the range input named `name` (`.settings-<name>`) with its value label. */
  private slider(name: string, value: number, set: (v: number) => void, label: (v: number) => string): void {
    const input = this.root.querySelector<HTMLInputElement>(`.settings-${name}`)!;
    const shown = input.parentElement!.querySelector<HTMLElement>(".settings-value")!;
    input.value = String(value);
    shown.textContent = label(value);
    input.addEventListener("input", () => {
      const v = Number(input.value);
      set(v);
      shown.textContent = label(v);
      this.changed();
    });
    // A focused input swallows game keys (see Input's isTyping); let go once dragged.
    input.addEventListener("change", () => input.blur());
  }

  private checkbox(name: string, value: boolean, set: (v: boolean) => void): void {
    const input = this.root.querySelector<HTMLInputElement>(`.settings-${name}`)!;
    input.checked = value;
    input.addEventListener("change", () => {
      set(input.checked);
      this.changed();
      input.blur();
    });
  }
}

const percent = (v: number) => `${Math.round(v * 100)}%`;
